/**
 * PixelArtPalette.ts — Extended color palette for pixel art rendering.
 *
 * Frost-themed 16-color palette for Gallowcrest:
 * icy blues, silvers, stone grays, rune gold, corruption purple, frostbite cyan.
 *
 * Each color has an index, a single-char alias for visual authoring,
 * and an RGB triplet. Index 0 is transparent (skip pixel).
 */

export interface PaletteEntry {
  index: number;
  char: string;
  name: string;
  r: number;
  g: number;
  b: number;
}

export const PALETTE: readonly PaletteEntry[] = [
  // ── Core palette (indices 0-15) ──
  { index: 0,  char: '.', name: 'transparent',      r: 0,   g: 0,   b: 0   },
  { index: 1,  char: '#', name: 'black',             r: 10,  g: 10,  b: 14  },
  { index: 2,  char: 'D', name: 'deep void',         r: 20,  g: 20,  b: 30  },
  { index: 3,  char: 'd', name: 'frost stone dark',  r: 42,  g: 42,  b: 58  },
  { index: 4,  char: 's', name: 'frost stone',       r: 60,  g: 60,  b: 80  },
  { index: 5,  char: 'S', name: 'stone gray',        r: 100, g: 100, b: 120 },
  { index: 6,  char: 'b', name: 'ice blue',          r: 74,  g: 158, b: 255 },
  { index: 7,  char: 'B', name: 'frost blue light',  r: 130, g: 190, b: 255 },
  { index: 8,  char: 'w', name: 'frost silver',      r: 192, g: 200, b: 216 },
  { index: 9,  char: 'W', name: 'icy white',         r: 220, g: 235, b: 255 },
  { index: 10, char: 'G', name: 'rune gold',         r: 212, g: 165, b: 32  },
  { index: 11, char: 'g', name: 'gold dim',          r: 160, g: 120, b: 30  },
  { index: 12, char: 'P', name: 'corruption purple', r: 138, g: 79,  b: 255 },
  { index: 13, char: 'C', name: 'frostbite cyan',    r: 0,   g: 212, b: 255 },
  { index: 14, char: 'R', name: 'blood red',         r: 180, g: 40,  b: 40  },
  { index: 15, char: 'E', name: 'ember orange',      r: 255, g: 140, b: 40  },

  // ── Extended colors (indices 16+) ──
  { index: 16, char: 'c', name: 'crystal blue',      r: 100, g: 200, b: 240 },
  { index: 17, char: 'p', name: 'pale purple',       r: 180, g: 140, b: 220 },
  { index: 18, char: 'r', name: 'rust',              r: 140, g: 70,  b: 50  },
  { index: 19, char: 'e', name: 'dark green',        r: 40,  g: 100, b: 60  },
  { index: 20, char: 'F', name: 'frost white',       r: 240, g: 245, b: 255 },
  { index: 21, char: 'n', name: 'snow',              r: 230, g: 235, b: 240 },
] as const;

/** Map from palette char to palette index. */
export const CHAR_TO_INDEX: Record<string, number> = {};
for (const entry of PALETTE) {
  CHAR_TO_INDEX[entry.char] = entry.index;
}

/** String of all palette chars in index order, for fast lookup. */
export const PALETTE_CHARS = PALETTE.map(e => e.char).join('');
